import { useCallback, useMemo, useState } from "react";
import { DateTime } from "luxon";
import type { ColumnDef } from "@tanstack/react-table";
import Table, { TableHeading, buildActionColumn, buildTagColumn } from "@/components/Table";
import EmptyState from "@/components/EmptyState";
import { actionId } from "@/hooks/useTableRowMenu";
import PersonSideSheet from "@/features/person/components/PersonSideSheet";
import type { Path } from "@/router";
import { useNavigate } from "@/router";
import type { RosterActivityEvent } from "@/features/roster-activity";
import { COHORT_LABELS, EVENT_GROUPS, KIND_LABELS } from "@/features/roster-activity";

type Props = {
  events: RosterActivityEvent[];
};

const COHORT_TAG_TYPE = {
  MEMBER: "tertiary",
  ASSOCIATE: "information",
  STUDENT: "secondary"
} as const;

type SideSheetTarget = {
  pgaId: string;
  linkPath?: Path | null;
  personName: string;
};

const GROUP_BODIES: Record<(typeof EVENT_GROUPS)[number]["key"], string> = {
  changes:
    "People who joined or left this section, or changed facility, job, title, chapter, or program during the selected date range.",
  retirements: "People who retired during the selected date range.",
  inMemoriam: "People who passed away during the selected date range."
};

function EmptyRosterActivity() {
  return (
    <EmptyState
      iconName="event_busy"
      title="No Roster Activity"
      caption="Try another week, month, or year, or adjust member type and change type filters."
    />
  );
}

const HIDE_COMPACT_LABEL = { headerClassName: "hide-header-in-column" } as const;

function buildEventColumns(
  onViewPerson: (event: RosterActivityEvent) => void,
  { includeChangeType = false }: { includeChangeType?: boolean } = {}
): ColumnDef<RosterActivityEvent, unknown>[] {
  const changeTypeColumn: ColumnDef<RosterActivityEvent, unknown> = {
    accessorKey: "kind",
    header: "Change Type",
    enableSorting: false,
    size: 160,
    meta: HIDE_COMPACT_LABEL,
    cell: ({ row }) => (
      <span className="text-body-medium text-sys-brand-on-surface whitespace-nowrap">
        {KIND_LABELS[row.original.kind]}
      </span>
    )
  };

  return [
    ...(includeChangeType ? [changeTypeColumn] : []),
    {
      accessorKey: "occurredAt",
      header: "Date",
      enableSorting: false,
      size: 140,
      meta: HIDE_COMPACT_LABEL,
      cell: ({ row }) => (
        <span className="text-body-medium text-sys-brand-on-surface-variant whitespace-nowrap">
          {DateTime.fromISO(row.original.occurredAt).toFormat("MMM d, yyyy")}
        </span>
      )
    },
    {
      accessorKey: "headline",
      header: "Activity",
      enableSorting: false,
      size: 280,
      meta: HIDE_COMPACT_LABEL,
      cell: ({ row }) => <span className="text-body-medium text-sys-brand-on-surface">{row.original.headline}</span>
    },
    {
      accessorKey: "detail",
      header: "Detail",
      enableSorting: false,
      size: 280,
      meta: HIDE_COMPACT_LABEL,
      cell: ({ row }) => (
        <span className="text-body-medium text-sys-brand-on-surface-variant">{row.original.detail?.trim() ?? ""}</span>
      )
    },
    buildTagColumn<RosterActivityEvent>({
      accessorKey: "cohort",
      header: "Program Type",
      getTagLabel: (event) => COHORT_LABELS[event.cohort],
      getTagType: (event) => COHORT_TAG_TYPE[event.cohort],
      getTagStyle: () => "standard",
      size: 100,
      minSize: 88
    }),
    buildActionColumn<RosterActivityEvent>({
      getActions: () => [],
      setMenu: () => undefined,
      actionId,
      showViewButton: (event) => Boolean(event.pgaId),
      onViewClick: onViewPerson,
      viewButtonTitle: "View person details",
      isRowNavigable: (event) => Boolean(event.pgaId && event.linkPath)
    })
  ];
}

export default function EventGroups({ events }: Props) {
  const navigate = useNavigate();
  const [sideSheet, setSideSheet] = useState<SideSheetTarget | null>(null);
  const onViewPerson = useCallback((event: RosterActivityEvent) => {
    if (!event.pgaId) return;
    setSideSheet({
      pgaId: event.pgaId,
      linkPath: event.linkPath,
      personName: event.personName
    });
  }, []);
  const changeColumns = useMemo(() => buildEventColumns(onViewPerson, { includeChangeType: true }), [onViewPerson]);
  const otherColumns = useMemo(() => buildEventColumns(onViewPerson), [onViewPerson]);
  const showFilteredEmptyChanges = events.length === 0;

  return (
    <div data-testid="roster-activity-event-groups">
      {EVENT_GROUPS.map((group) => {
        const grouped = events.filter((event) => group.kinds.includes(event.kind));
        const showEmptyChangesTable = group.key === "changes" && showFilteredEmptyChanges;
        if (!grouped.length && !showEmptyChangesTable) return null;

        const heading = `${group.title} (${grouped.length})`;

        return (
          <section key={group.key} aria-label={heading}>
            <TableHeading heading={heading} body={GROUP_BODIES[group.key]} />
            <Table
              data={grouped}
              loading={false}
              columns={group.key === "changes" ? changeColumns : otherColumns}
              getRowId={(event) => event.id}
              getRowLink={(row) => {
                if (!row.original.pgaId || !row.original.linkPath) return null;
                return {
                  to: row.original.linkPath,
                  "aria-label": `View ${row.original.personName}`
                };
              }}
              hasActiveFilters={showEmptyChangesTable}
              renderFilteredEmptyState={showEmptyChangesTable ? () => <EmptyRosterActivity /> : undefined}
              data-testid={`roster-activity-group-table-${group.key}`}
            />
          </section>
        );
      })}
      <PersonSideSheet
        open={!!sideSheet}
        onClose={() => setSideSheet(null)}
        pgaId={sideSheet?.pgaId ?? null}
        showPersonalDetails
        showEmployment
        customActionButtons={
          sideSheet?.linkPath
            ? [
                {
                  text: "View Person",
                  variant: "outlined",
                  testId: "roster-activity-view-person",
                  onClick: () => {
                    const linkPath = sideSheet.linkPath;
                    if (!linkPath) return;
                    navigate(linkPath);
                    setSideSheet(null);
                  }
                }
              ]
            : undefined
        }
      />
    </div>
  );
}
